import React from 'react';
import logger from '../../utils/logger';
import { showError } from '../../utils/sweetAlert';
import {
  TableWrapper,
  LoadingSpinner,
  ErrorMessage,
  EmptyState,
  ServerPagination
} from './AdminTableComponents';
import TableSkeleton from '../ui/TableSkeleton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Transactions Section Component
const TransactionsSection = ({
  transactions,
  loading,
  error,
  pagination,
  setPagination,
  search,
  setSearch,
  statusFilter,
  setStatusFilter,
  currencyFilter,
  setCurrencyFilter,
  loadTransactions,
  getStatusBadge
}) => {
  const downloadFile = async (url, filename) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/${url}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        await showError(
          'Download Failed',
          'Failed to download file. Please try again.'
        );
      }
    } catch (error) {
      logger.error('Download error:', error);
      await showError(
        'Download Error',
        'Error downloading file. Please check your connection.'
      );
    }
  };

  return (
    <div>
      <div className="md:flex md:items-center md:justify-between mb-6">
        <div>
          <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
            Transaction History
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            View all payment transactions and invoices
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button
            onClick={loadTransactions}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 grid grid-cols-1 md:grid-cols-4 gap-4">
        <input
          type="text"
          placeholder="Search by email or transaction ID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-emerald-500 focus:border-emerald-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-emerald-500 focus:border-emerald-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        >
          <option value="all">All Status</option>
          <option value="completed">Completed</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
          <option value="refunded">Refunded</option>
        </select>
        <select
          value={currencyFilter}
          onChange={(e) => setCurrencyFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-emerald-500 focus:border-emerald-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        >
          <option value="all">All Currencies</option>
          <option value="AUD">AUD</option>
          <option value="USD">USD</option>
        </select>
      </div>

      {error && <ErrorMessage message={error} />}

      {loading ? (
        <TableSkeleton rows={5} cols={6} />
      ) : transactions.length === 0 ? (
        <EmptyState
          icon="💳"
          title="No transactions found"
          description="Try adjusting your search or filters."
        />
      ) : (
        <>
          <TableWrapper>
            <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Email Sent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {transactions.map((transaction) => (
                  <tr
                    key={transaction.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {transaction.customer_email || 'N/A'}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {transaction.package_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        ${transaction.amount?.toFixed(2)} {transaction.currency}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(transaction.payment_status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {transaction.email_sent ? (
                          <span className="text-green-600 dark:text-green-400 text-sm">
                            ✅ Yes
                          </span>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500 text-sm">
                            ❌ No
                          </span>
                        )}
                        {transaction.invoice_sent && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            (Invoice sent)
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(transaction.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      {transaction.report_available && (
                        <button
                          onClick={() =>
                            downloadFile(
                              `payments/${transaction.id}/report/download`,
                              `report_${transaction.id}.pdf`
                            )
                          }
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                        >
                          📄 Report
                        </button>
                      )}
                      {transaction.invoice_available && (
                        <button
                          onClick={() =>
                            downloadFile(
                              `payments/${transaction.id}/invoice/download`,
                              `invoice_${transaction.id}.pdf`
                            )
                          }
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300"
                        >
                          🧾 Invoice
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrapper>
          <ServerPagination pagination={pagination} setPagination={setPagination} />
        </>
      )}
    </div>
  );
};

export default TransactionsSection;


